'use client';

import { PlanTierSelector } from '@hyphengroup/cms-components';
import { CONTENT_TRANSLATIONS } from '@hyphengroup/cms-components/cms-blocks/PlanTierSelector/i18n';
import type { PlanTierSummarySource } from '@hyphengroup/cms-components/cms-blocks/PlanTierSelector/types';
import { getClientTranslations } from '@hyphengroup/cms-components/utils/translations/getClientTranslations';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { useEffect } from 'react';
import { brandSpecific } from 'utils/override';
import type { CmsTrustStripComponent } from '$/(cms)/_modules/cmsService/cmsServiceGql/mapper/components/TrustStrip';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import type { CmsPlanTierSelectorComponent } from '$/(cms)/_modules/cmsService/types/components';
import { TISearchBanner } from '$/components/cms/components-user/travel/TravelPolicySearch/TISearchBanner';
import { useTiTiers } from '$/components/cms/components-user/travel/useTiTiers';
import { TrustStripWrapper } from '$/components/cms/components-user/TrustStrip/TrustStripWrapper';
import { pushDataLayer } from '$/components/cms/pushDataLayer';
import { useClientContext } from '$/modules/context/client';

type PlanTierSelectorWrapperProps = {
  component: CmsPlanTierSelectorComponent;
};

// Brand display name embedded in the panel discount highlight copy (CMS-1864
// Q5), via the repo brandSpecific() convention. The wrapper is gated to
// singsaver / moneyhero below: singsaver is set explicitly and moneyhero
// resolves through the `_` default — so `_` is the live value for moneyhero
// here, not just a type-level placeholder.
const BRAND_DISPLAY_NAME = brandSpecific({
  _: 'MoneyHero',
  singsaver: 'SingSaver',
});

/**
 * M3b Plan Tier Selector wrapper (CMS-1724). Brand-gated to SingSaver (SG) and
 * MoneyHero (HK) — money101 / moneymax render nothing. Tier building, premium
 * matching and the active-tier product are shared via `useTiTiers` (also
 * consumed by the M5 nav dropdown, the sticky footer, the TI ProductDetails
 * section and the M4 column highlight); this wrapper adds the M3 summary-panel
 * data and the M3a Trust Strip slot on top of it.
 *
 * All copy resolution and value formatting live in the block (CMS-1864), so this
 * is a pure data/container layer — the only string it renders is the no-products
 * banner below.
 *
 * Selection persists across a premium re-search (carried by `setTravelQuotes`)
 * and resets when the trip type toggles (the effect keyed on trip type only).
 * The cards show the hook's effective tier as selected (CMS-2054).
 */
export const PlanTierSelectorWrapper = ({
  component,
}: PlanTierSelectorWrapperProps): JSX.Element | null => {
  const context = useClientContext();
  const {
    pageData: { travelQuotes, page },
  } = usePageDataContext();
  const {
    tiers,
    effectiveTierId,
    defaultTierId,
    recommendedBadgeLabel,
    setSelectedTier,
    activeTripType,
    isAnnual,
    currencyCode,
    searchCompleted,
  } = useTiTiers(component);

  // Reset the selection when the trip type toggles (single ↔ annual). Keyed on
  // the trip type only, so a premium re-search with the same type keeps the
  // current selection (which setTravelQuotes carries over — see CMS-1724 T15).
  useEffect(() => {
    setSelectedTier(undefined);
  }, [activeTripType, setSelectedTier]);

  if (context.brand !== 'singsaver' && context.brand !== 'moneyhero') {
    return null;
  }

  // No product configured for the active trip type — show the "no products"
  // banner in place of the selector. Tiers whose premium hasn't resolved stay
  // in the list as loading skeletons, so this no longer fires on empty results.
  //
  // The Trust Strip is intentionally NOT rendered in this state: it shows
  // cross-product trust factors, which are meaningless when the provider has no
  // products for the active trip type, and the whole M3 block is replaced by
  // this banner. PageContent already skipped the standalone Trust Strip (a
  // visible selector is present), so it correctly renders nowhere here.
  //
  // This banner is the only string the wrapper renders itself, so it resolves
  // just the one key from the block's co-located copy table.
  if (searchCompleted && tiers.length === 0) {
    const content = getClientTranslations(CONTENT_TRANSLATIONS, context);
    return <TISearchBanner message={tr('noProductsAvailable', content)} />;
  }

  // Summary panel data (CMS-1864): the effective tier, else the ungated default
  // so the desktop panel is never empty on load (CMS-2054). An index too: the
  // panel's Apply CTA reports it as its tracking `index` (CMS-1795).
  const summaryTierId = effectiveTierId ?? defaultTierId;
  const activeTierIndex = tiers.findIndex((tier) => tier.id === summaryTierId);
  const activeTier = activeTierIndex >= 0 ? tiers[activeTierIndex] : undefined;
  let summary: PlanTierSummarySource | undefined;
  if (activeTier && travelQuotes) {
    const { product } = activeTier;
    const { params } = travelQuotes;
    const countries = travelQuotes.countries ?? [];
    const regions = travelQuotes.regions ?? [];

    // Destination labels resolved from CMS country data for the selected ISO
    // codes; a region-based search (no explicit countries) falls back to the
    // region label. This is data resolution (not copy), so it stays here; the
    // panel only joins the labels with the localized separator.
    const destinationLabels = (() => {
      const countryLabels = (params.destinationIsoCodes ?? [])
        .map((iso) => countries.find((country) => country.iso === iso)?.label)
        .filter((label): label is string => !!label);
      if (countryLabels.length > 0) {
        return countryLabels;
      }
      const region = regions.find(
        (candidate) => candidate.value === params.region,
      )?.label;
      return region ? [region] : [];
    })();

    summary = {
      // Full product name, never the per-tier display override (Q2). Do not
      // fall back to activeTier.name — that starts from displayNameOverride and would
      // reintroduce the override into the panel title.
      title: product.product_name || product.title || '',
      providerName: component.provider.providerName,
      providerLogoUrl: component.provider.providerImage || undefined,
      // Discount highlight strip (Q5) is built in the block from this percent;
      // the % is not shown next to the price.
      discountPercent: activeTier.price?.discountPercent ?? 0,
      brandName: BRAND_DISPLAY_NAME.get(context.brand),
      // Same raw premium as the tier card; the panel formats it (SGD $24) with
      // its own currency-code prefix per Figma 21456:8084.
      price: activeTier.price,
      isPriceLoading: activeTier.isPriceLoading,
      trip: {
        destinationLabels,
        startDate: params.startDate,
        endDate: params.endDate,
        noOfAdults: params.noOfAdults,
        noOfChildren: params.noOfChildren,
        travelAsFamily: params.travelAsFamily,
      },
      product,
      index: activeTierIndex,
    };
  }

  // M3a Trust Strip is authored as a sibling CMS block. On the TI provider page
  // the (cms) *Content component drops it from the standalone BlockMapper stack
  // when a Plan Tier Selector is present, and we render it here at the top of
  // the left column so the summary panel spans beside it as one M3 block
  // (Figma 21456:8078). `inline` skips the standalone gap-compensation hack.
  const trustStrip = page?.components?.find(
    (candidate): candidate is CmsTrustStripComponent =>
      candidate?.__typename === 'TrustStripComponent',
  );

  return (
    <PlanTierSelector
      tiers={tiers}
      selectedTierId={effectiveTierId}
      recommendedBadgeLabel={recommendedBadgeLabel}
      onSelectTier={setSelectedTier}
      isAnnual={isAnnual}
      currencyCode={currencyCode}
      summary={summary}
      pushDataLayer={pushDataLayer}
      leadingSlot={
        // Only pass the slot when the Trust Strip is set to render; a
        // visible:false / empty strip would render null anyway (flex `gap`
        // reserves no space for a null child), but skipping it keeps the intent
        // explicit and consistent with the PageContent brand/visible gate.
        trustStrip && trustStrip.visible !== false ? (
          <TrustStripWrapper {...trustStrip} inline />
        ) : undefined
      }
    />
  );
};
